import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallerCountry } from "@/hooks/useCallerCountry";

export interface RateBreakdownRow {
  destinationName: string;
  destinationType: "mobile" | "landline" | "fixed" | string;
  retailRateAudIncGstCents: number;
  billingIncrement: string;
  dialCode: string | null;
}

interface RpcRow {
  country_code: string;
  country_name: string;
  destination_name: string;
  destination_type: string;
  retail_rate_cents: number;
  retail_rate_aud_inc_gst_cents: number;
  billing_increment: string | null;
  dial_code: string | null;
}

/**
 * Fetches the per-carrier (destination_name) rate breakdown for a country code,
 * sourced live from `voice_routes` via the `get_country_rate_breakdown` RPC.
 *
 * Columns: `retail_rate_aud_inc_gst_cents` is the AUD list (GST included) and
 * `retail_rate_cents` the USD list, both pushed from the switch per currency
 * since 14 Sep 2026. The caller picks which list the rows carry.
 */

export function useCountryRateBreakdown(countryCode: string | null | undefined, currency: "AUD" | "USD" = "AUD") {
  const { formatLocalRate, formatLocalAmount } = useCallerCountry();

  const query = useQuery({
    queryKey: ["country-rate-breakdown", countryCode, currency],
    enabled: !!countryCode,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 6 * 60 * 60 * 1000,
    queryFn: async (): Promise<RateBreakdownRow[]> => {
      const { data, error } = await supabase.rpc("get_country_rate_breakdown", {
        p_country_code: countryCode!,
      });
      if (error) throw error;

      const rows = (data ?? []) as RpcRow[];
      return rows.map((r) => ({
        destinationName: r.destination_name,
        destinationType: (r.destination_type || "landline").toLowerCase(),
        // Field name is historical; it carries whichever list the caller asked for.
        retailRateAudIncGstCents: currency === "USD" ? Number(r.retail_rate_cents) || 0 : Number(r.retail_rate_aud_inc_gst_cents) || 0,
        billingIncrement: r.billing_increment || "60/60",
        dialCode: r.dial_code,
      })).filter((row) => currency !== "USD" || row.retailRateAudIncGstCents > 0);
    },
  });

  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    formatLocalRate,
    formatLocalAmount,
  };
}
